import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, Calendar, CalendarX2, ChevronLeft, ChevronRight, Eye, RefreshCw, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import api from "../../api/axios";
import AdminNavbar from "../../components/layout/AdminNavbar";

const dateKey = (date) => [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
].join("-");

const parseDateKey = (value) => {
    const [year, month, day] = String(value || "").slice(0, 10).split("-").map(Number);
    return new Date(year, month - 1, day);
};

const formatDate = (value) => parseDateKey(value).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
});

const getInitials = (name) => {
    if (!name) return "E";
    const parts = name.trim().split(/\s+/);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return parts[0].slice(0, 2).toUpperCase();
};

const getCycle = (offset) => {
    const today = new Date();
    const currentStart = today.getDate() >= 25
        ? new Date(today.getFullYear(), today.getMonth(), 25)
        : new Date(today.getFullYear(), today.getMonth() - 1, 25);
    const start = new Date(currentStart.getFullYear(), currentStart.getMonth() + offset, 25);
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 24);
    return { start, end, startKey: dateKey(start), endKey: dateKey(end) };
};

const AbsenceWithoutLeave = () => {
    const navigate = useNavigate();
    const [cycleOffset, setCycleOffset] = useState(0);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const cycle = useMemo(() => getCycle(cycleOffset), [cycleOffset]);

    const loadAbsences = useCallback(async (forceRefresh = false) => {
        setLoading(true);
        try {
            const response = await api.get("/api/admin/attendance", {
                cache: !forceRefresh,
                params: {
                    startDate: cycle.startKey,
                    endDate: cycle.endKey,
                    includeAbsenceSummary: true,
                    onlyAbsenceSummary: true,
                    includeEscalations: false,
                },
            });
            const missingDays = Array.isArray(response.data?.absentEmployeeDays)
                ? response.data.absentEmployeeDays
                : [];

            const grouped = new Map();
            missingDays.forEach(({ employee, date }) => {
                if (!employee?._id || !date) return;
                if (!grouped.has(employee._id)) grouped.set(employee._id, { employee, dates: [] });
                grouped.get(employee._id).dates.push(date);
            });

            setRows(Array.from(grouped.values())
                .map((item) => ({ ...item, dates: [...new Set(item.dates)].sort() }))
                .sort((a, b) => String(a.employee?.name || "").localeCompare(String(b.employee?.name || ""))));
        } catch (error) {
            toast.error(error.response?.data?.message || "Failed to load absence details.");
            setRows([]);
        } finally {
            setLoading(false);
        }
    }, [cycle.endKey, cycle.startKey]);

    useEffect(() => {
        document.title = "Absence Without Leave | Savrion Technologies"; loadAbsences(); }, [loadAbsences]);

    const visibleRows = useMemo(() => {
        const query = search.trim().toLowerCase();
        return rows.filter(({ employee }) => !query || [employee?.name, employee?.employeeId, employee?.team]
            .some((value) => String(value || "").toLowerCase().includes(query)));
    }, [rows, search]);

    useEffect(() => {
        document.title = "Absence Without Leave | Savrion Technologies";
        setCurrentPage(1);
    }, [search, cycleOffset, perPage]);

    const totalDays = rows.reduce((sum, item) => sum + item.dates.length, 0);

    const totalPages = useMemo(() => {
        if (perPage === "All") return 1;
        return Math.ceil(visibleRows.length / Number(perPage)) || 1;
    }, [visibleRows.length, perPage]);

    const paginatedRows = useMemo(() => {
        if (perPage === "All") return visibleRows;
        const size = Number(perPage);
        const start = (currentPage - 1) * size;
        return visibleRows.slice(start, start + size);
    }, [visibleRows, currentPage, perPage]);

    const paginationControls = visibleRows.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 p-4 border-b border-slate-100 dark:border-slate-800 bg-[var(--card-bg)] text-xs font-bold text-slate-500">
            <div className="flex items-center gap-2">
                <span>Show per page:</span>
                <select
                    value={perPage}
                    onChange={(e) => setPerPage(e.target.value === "All" ? "All" : Number(e.target.value))}
                    className="h-8 px-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-[var(--bg-primary)] text-xs font-bold text-[var(--text-primary)] outline-none cursor-pointer"
                >
                    <option value={10}>10</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                    <option value="All">All</option>
                </select>
                <span className="ml-2 text-slate-400">
                    Showing {perPage === "All" ? 1 : Math.min((currentPage - 1) * Number(perPage) + 1, visibleRows.length)} to {perPage === "All" ? visibleRows.length : Math.min(currentPage * Number(perPage), visibleRows.length)} of {visibleRows.length} entries
                </span>
            </div>

            {perPage !== "All" && totalPages > 1 && (
                <div className="flex items-center gap-1.5">
                    <button type="button" disabled={currentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))} className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-[var(--bg-primary)] hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors cursor-pointer">Previous</button>
                    <span className="px-3 py-1.5 font-black text-[var(--accent-primary)] dark:text-[var(--accent-primary)]">Page {currentPage} of {totalPages}</span>
                    <button type="button" disabled={currentPage >= totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))} className="px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 bg-[var(--bg-primary)] hover:bg-slate-100 dark:hover:bg-slate-800 disabled:opacity-40 transition-colors cursor-pointer">Next</button>
                </div>
            )}
        </div>
    );

    return (
        <div className="flex flex-col xl:flex-row min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]">
            <AdminNavbar />
            <main className="min-w-0 flex-1 p-3 sm:p-5 lg:p-6 overflow-x-hidden">
                <div className="w-full space-y-5">
                    {/* METRICS CARDS ROW MATCHING REFERENCE DESIGN */}
                    <section className="grid gap-4 sm:grid-cols-2">
                        {/* EMPLOYEES AFFECTED CARD */}
                        <div className="flex items-center gap-4 rounded-2xl border border-slate-100 dark:border-slate-800 bg-[var(--card-bg)] p-5 shadow-xs">
                            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-red-100/80 dark:bg-red-500/20 text-red-500">
                                <AlertTriangle size={22} />
                            </div>
                            <div>
                                <strong className="block text-3xl font-black tracking-tight text-[var(--text-primary)]">{rows.length}</strong>
                                <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mt-0.5">
                                    EMPLOYEES AFFECTED
                                </span>
                            </div>
                        </div>

                        {/* ABSENCE DAYS WITHOUT APPLICATION CARD */}
                        <div className="flex items-center gap-4 rounded-2xl border border-slate-100 dark:border-slate-800 bg-[var(--card-bg)] p-5 shadow-xs">
                            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-[var(--accent-soft)] dark:bg-[var(--accent-subtle)] text-[var(--accent-primary)] dark:text-[var(--accent-primary)]">
                                <Calendar size={22} />
                            </div>
                            <div>
                                <strong className="block text-3xl font-black tracking-tight text-[var(--text-primary)]">{totalDays}</strong>
                                <span className="text-[11px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-wider block mt-0.5">
                                    ABSENCE DAYS WITHOUT APPLICATION
                                </span>
                            </div>
                        </div>
                    </section>

                    {/* CYCLE PICKER & SEARCH BAR ROW */}
                    <section className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3">
                        <div className="flex items-center gap-2 rounded-2xl border border-slate-100 dark:border-slate-800 bg-[var(--card-bg)] p-2 shadow-xs shrink-0">
                            <button
                                type="button"
                                onClick={() => setCycleOffset((value) => Math.max(-12, value - 1))}
                                disabled={cycleOffset <= -12}
                                className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--bg-secondary)] dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 transition-colors cursor-pointer"
                            >
                                <ChevronLeft size={18} />
                            </button>
                            <div className="px-3 text-center min-w-[190px]">
                                <span className="block text-[10px] font-black uppercase tracking-wider text-[var(--accent-primary)] dark:text-[var(--accent-primary)]">
                                    {cycleOffset === 0 ? "CURRENT CYCLE" : "PREVIOUS CYCLE"}
                                </span>
                                <strong className="text-xs font-bold text-[var(--text-primary)] block mt-0.5">
                                    {formatDate(cycle.startKey)} — {formatDate(cycle.endKey)}
                                </strong>
                            </div>
                            <button
                                type="button"
                                onClick={() => setCycleOffset((value) => Math.min(0, value + 1))}
                                disabled={cycleOffset >= 0}
                                className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--bg-secondary)] dark:bg-slate-800 hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-300 disabled:opacity-40 transition-colors cursor-pointer"
                            >
                                <ChevronRight size={18} />
                            </button>
                        </div>
                        <div className="relative flex-1">
                            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                                placeholder="Search employee, ID or team..."
                                className="h-12 w-full rounded-2xl border border-slate-100 dark:border-slate-800 bg-[var(--card-bg)] pl-11 pr-4 text-sm font-semibold text-[var(--text-primary)] placeholder:text-slate-400 outline-none focus:ring-2 focus:ring-[var(--accent-primary)]/20 transition-all shadow-xs"
                            />
                        </div>
                    </section>

                    {/* MAIN TABLE CONTAINER MATCHING REFERENCE SCREENSHOT */}
                    <section className="overflow-hidden rounded-2xl border border-slate-100 dark:border-slate-800 bg-[var(--card-bg)] shadow-xs">
                        {paginationControls}
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[900px] border-collapse text-left text-sm">
                                <colgroup>
                                    <col style={{ width: "24%" }} />
                                    <col style={{ width: "54%" }} />
                                    <col style={{ width: "10%" }} />
                                    <col style={{ width: "12%" }} />
                                </colgroup>
                                <thead className="bg-[var(--bg-secondary)]/80 dark:bg-slate-800/50 text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500">
                                    <tr>
                                        <th className="border-r border-slate-100 dark:border-slate-800 px-6 py-4">EMPLOYEE</th>
                                        <th className="border-r border-slate-100 dark:border-slate-800 px-6 py-4">ABSENT DATES WITHOUT APPLICATION</th>
                                        <th className="border-r border-slate-100 dark:border-slate-800 px-6 py-4 text-center">DAYS</th>
                                        <th className="px-6 py-4 text-center">ACTION</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {loading && rows.length === 0 ? (
                                        Array.from({ length: 5 }).map((_, idx) => (
                                            <tr key={`skeleton-${idx}`} className="animate-pulse">
                                                <td className="border-r border-slate-100 dark:border-slate-800 px-6 py-5">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 bg-slate-200 dark:bg-slate-700 rounded-full shrink-0" />
                                                        <div className="space-y-1.5 flex-1">
                                                            <div className="h-4 w-32 bg-slate-200 dark:bg-slate-700 rounded" />
                                                            <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded" />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="border-r border-slate-100 dark:border-slate-800 px-6 py-5">
                                                    <div className="flex gap-2">
                                                        <div className="h-7 w-22 bg-slate-200 dark:bg-slate-700 rounded-xl" />
                                                        <div className="h-7 w-22 bg-slate-200 dark:bg-slate-700 rounded-xl" />
                                                    </div>
                                                </td>
                                                <td className="border-r border-slate-100 dark:border-slate-800 px-6 py-5 text-center">
                                                    <div className="h-7 w-16 bg-slate-200 dark:bg-slate-700 rounded-xl mx-auto" />
                                                </td>
                                                <td className="px-6 py-5 text-center">
                                                    <div className="h-9 w-20 bg-slate-200 dark:bg-slate-700 rounded-xl mx-auto" />
                                                </td>
                                            </tr>
                                        ))
                                    ) : !visibleRows.length ? (
                                        <tr>
                                            <td colSpan={4} className="p-12 text-center">
                                                <CalendarX2 size={42} className="mx-auto text-emerald-500" />
                                                <h2 className="mt-3 text-lg font-extrabold text-[var(--text-primary)]">No Unmatched Absences Found</h2>
                                                <p className="mt-1 text-xs font-semibold text-slate-400">Every absence in this active cycle has a corresponding leave application.</p>
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedRows.map(({ employee, dates }) => (
                                            <tr key={employee._id} className="transition-colors hover:bg-[var(--bg-secondary)]/50 dark:hover:bg-slate-800/30">
                                                {/* EMPLOYEE COLUMN */}
                                                <td className="border-r border-slate-100 dark:border-slate-800 px-6 py-5 align-middle">
                                                    <div className="flex items-center gap-3">
                                                        <div className="w-10 h-10 shrink-0 rounded-full bg-[var(--accent-light)] dark:bg-[var(--accent-subtle)] text-[var(--accent-primary)] dark:text-[var(--heading-secondary)] font-bold text-xs flex items-center justify-center border border-[var(--accent-soft)] dark:border-[var(--border-color)]">
                                                            {getInitials(employee.name)}
                                                        </div>
                                                        <div className="min-w-0">
                                                            <strong className="block text-sm font-bold text-slate-800 dark:text-slate-100 truncate">
                                                                {employee.name}
                                                            </strong>
                                                            <span className="block text-xs font-medium text-slate-400 dark:text-slate-400 truncate capitalize mt-0.5">
                                                                {employee.team || "General"}{employee.role ? ` • ${employee.role}` : ""}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </td>

                                                {/* ABSENT DATES WITHOUT APPLICATION COLUMN */}
                                                <td className="border-r border-slate-100 dark:border-slate-800 px-6 py-5 align-middle">
                                                    <div className="flex flex-wrap gap-2.5 max-h-48 overflow-y-auto p-0.5">
                                                        {dates.map((date) => (
                                                            <span
                                                                key={date}
                                                                className="bg-rose-50/90 dark:bg-rose-950/30 border border-rose-100 dark:border-rose-800/30 text-rose-500 dark:text-rose-400 font-bold text-xs px-3 py-1.5 rounded-xl shadow-2xs inline-block hover:bg-rose-100/80 transition-colors"
                                                            >
                                                                {formatDate(date)}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </td>

                                                {/* DAYS COLUMN */}
                                                <td className="border-r border-slate-100 dark:border-slate-800 px-6 py-5 text-center align-middle">
                                                    <span className="inline-flex items-center justify-center bg-rose-50/90 dark:bg-rose-950/30 text-rose-500 dark:text-rose-400 font-bold text-xs px-3.5 py-1.5 rounded-xl">
                                                        {dates.length} Days
                                                    </span>
                                                </td>

                                                {/* ACTION COLUMN */}
                                                <td className="px-6 py-5 text-center align-middle">
                                                    <button
                                                        type="button"
                                                        onClick={() => navigate("/admin/employees", { state: { openEmployeeId: employee._id, openTab: "leaves" } })}
                                                        className="inline-flex items-center justify-center gap-1.5 bg-[var(--accent-primary)] hover:bg-[var(--accent-hover)] text-white font-bold text-xs px-4 py-2 rounded-xl shadow-md shadow-[var(--accent-primary)]/20 transition-all cursor-pointer"
                                                    >
                                                        <Eye size={14} />
                                                        <span>View</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                    </section>
                </div>
            </main>
        </div>
    );
};

export default AbsenceWithoutLeave;


